import axios from "axios";
import { Preferences } from "@capacitor/preferences";
import * as tokenStore from "./tokenStore";
import { Capacitor, CapacitorHttp, HttpResponse } from "@capacitor/core";
import { Network } from "@capacitor/network";
import {
  enhancedResponseInterceptor,
  enhancedRequestInterceptor,
} from "./apiErrorInterceptor";

// Base API URLs based on environment
// API environments configuration
export const API_ENVIRONMENTS = {
  production: "https://data.arc.abmrc.com",
  staging: "https://data1.arc.abmrc.com",
  development: "https://dev.arc.abmrc.com",
  local: "http://localhost:1337",
  dev: "https://dev.arc.abmrc.com",
};

// Current environment is decided at BUILD TIME via REACT_APP_ENV, injected by
// the npm scripts (see package.json) — never by editing this file.
//   npm start / npm run build        -> staging   (safe default)
//   npm run start:dev                -> development
//   npm run start:local              -> local
//   npm run build:production         -> production (explicit only)
// Anything missing or unrecognised (e.g. "prod" instead of "production") falls
// back to staging, so a build can never silently point somewhere unintended.
type ApiEnv = keyof typeof API_ENVIRONMENTS;

const DEFAULT_ENV: ApiEnv = "development"; // Default to development for safety

const resolveEnv = (value: string | undefined): ApiEnv => {
  if (value && value in API_ENVIRONMENTS) {
    return value as ApiEnv;
  }
  if (value) {
    console.warn(
      `Unknown REACT_APP_ENV "${value}". Expected one of ${Object.keys(
        API_ENVIRONMENTS
      ).join(", ")}. Falling back to "${DEFAULT_ENV}".`
    );
  }
  return DEFAULT_ENV;
};

export const CURRENT_ENV = resolveEnv(process.env.REACT_APP_ENV);

// Base API URL based on environment.
// REACT_APP_BLAZE_URL overrides it, mirroring REACT_APP_KERNEL_URL below. Needed for any
// test from a real device: the `local` entry is `http://localhost:1337`, and on a phone
// `localhost` is the phone. Without the override, device testing means editing this map.
export const BASE_URL =
  // REACT_APP_BLAZE_URL is the name to use now that there are two backends; the older
  // REACT_APP_API_URL is still honoured so existing web build configs keep working.
  process.env.REACT_APP_BLAZE_URL ||
  process.env.REACT_APP_API_URL ||
  API_ENVIRONMENTS[CURRENT_ENV] ||
  API_ENVIRONMENTS.development;

// For debugging
console.log(`API running in ${CURRENT_ENV} mode with URL: ${BASE_URL}`);

const isWeb = Capacitor.getPlatform() === 'web';

/**
 * The transport signal the kernel classifies on (D23 / ADR-0016).
 *
 * ⚠️ PLATFORM-DERIVED, never a constant. Android's WebView origin is byte-identical to the
 * production web origin, so `Origin` cannot distinguish them and the kernel prefers this
 * header — and its own test suite pins that this header WINS over a matching web origin.
 * A web build hardcoding "mobile" would therefore be handed body tokens into JavaScript
 * reach instead of HttpOnly cookies, silently, with nothing erroring. That is HIPAA #6, and
 * it is D7's silent-downgrade trap with the polarity reversed.
 *
 * "mobile" is the wire contract (docs/auth-api-contract.md); the kernel also accepts
 * "native" as an alias.
 */
export const X_CLIENT = isWeb ? "web" : "mobile";

// ── Kernel origin (parallel run, Slice A — D4/D21) ───────────────────────────
//
// Two backends during the parallel run. The routing rule (D21) is a PREFIX rule, not a
// per-path table: `/api/v3/*` goes to the kernel, everything else goes to blaze. That is
// why there is no 47-row tag map here — blaze is the default and always has been.
//
// ⚠️ NO SILENT FALLBACK. `_is_web_request`'s sibling trap (D7) is the model: a wrong or
// missing origin that quietly resolves to *something* is worse than one that fails. An
// unset kernel origin yields "" and `kernelClient` throws on first use with a message
// naming the fix, rather than sending auth traffic to blaze and 404ing mysteriously.
//
// Verified from the kernel's own deploy config (deploy/nginx/arc-kernel-api.conf): the
// shared nginx for the dev domain proxies `/api/v3/` to the kernel on 127.0.0.1:7160.
// So on DEV the two backends are on DIFFERENT origins — kernel at dev-arcconnect,
// blaze at 172.16.0.94:1337.
//
// staging/production are intentionally left to `REACT_APP_KERNEL_URL` for now: the plan
// is for both backends to share ONE origin there (D4 phase 2, path-routed), at which
// point KERNEL_URL and BLAZE_URL converge and only this map changes — no call site does.
//
// ⚠️ The `/api/v3` suffix belongs in the BASE URL, not in each path. D21's rule is a
// prefix rule; keeping it here means call sites write `/auth/login/start` and stay
// correct when the origins collapse.
export const KERNEL_ENVIRONMENTS: Record<keyof typeof API_ENVIRONMENTS, string> = {
  production: "",
  staging: "",
  development: "https://dev-arcconnect.abmrc.com/api/v3",
  local: "http://localhost:8000/api/v3",
  // `dev` is dev/web's alias for the same environment as `development`.
  dev: "https://dev-arcconnect.abmrc.com/api/v3",
};

export const KERNEL_URL =
  process.env.REACT_APP_KERNEL_URL || KERNEL_ENVIRONMENTS[CURRENT_ENV] || "";

// ── Provider portal URL (R-734, R-753) ───────────────────────────────────────
//
// Staff can switch to the provider portal from here; see services/staffPortal.ts.
//
// WEB: the patient app and the portal share one host, so the default is this origin's
// `/portal/`. NATIVE: the WebView origin is `capacitor://localhost` / `https://localhost`,
// which says nothing about the deploy, so the portal is looked up per environment and
// opened in the system browser.
//
// The staging and production `/portal/` paths are CONFIRMED (owner, 2026-09-28):
// `staging-arcconnect.abmrc.com/portal/` and `arcconnect.abmrc.com/portal/`. `local` has no
// known portal: set REACT_APP_PORTAL_URL for native device testing against a local stack.
export const PORTAL_ENVIRONMENTS: Record<keyof typeof API_ENVIRONMENTS, string> = {
  production: "https://arcconnect.abmrc.com/portal/",
  staging: "https://staging-arcconnect.abmrc.com/portal/",
  development: "https://dev-arcconnect.abmrc.com/portal/",
  local: "",
  dev: "https://dev-arcconnect.abmrc.com/portal/",
};

/** The provider portal's URL. REACT_APP_PORTAL_URL overrides on every platform. */
export const getPortalUrl = (): string => {
  const override = process.env.REACT_APP_PORTAL_URL;
  if (override) return override;
  if (Capacitor.getPlatform() === "web") {
    return `${window.location.origin}/portal/`;
  }
  return PORTAL_ENVIRONMENTS[CURRENT_ENV] || PORTAL_ENVIRONMENTS.development;
};

// blaze keeps the original name so ~40 existing call sites stay untouched (D6 blast
// radius). `blazeClient` is the honest name; `apiClient` is retained as its alias.
export const BLAZE_URL = BASE_URL;

// Singleton refresh promise — shared by both clients so concurrent 401s
// all await the same refresh call instead of each spawning their own.
/**
 * Did the refresh fail because the SESSION is dead, or because the refresh could not be
 * completed right now?
 *
 * ⚠️ The distinction decides whether the user keeps their session. Every refresh failure
 * used to run clearAuthData() + auth:logout, which means a **429 signed the user out** --
 * and `refresh` is rate-limited per IP (60/60s, 600/hour in `rate_limit_rule`), so a NAT'd
 * office or a runaway client ejects people who did nothing wrong and whose refresh token is
 * perfectly valid.
 *
 * Transient: 429, any 5xx, and a rejection with no response at all (network/DNS/timeout --
 * the server never rendered a verdict, so we must not invent one). Everything else, notably
 * 401/403, means the refresh token really is dead and the session must go.
 *
 * The cost of being wrong in the tolerant direction is bounded: the access token stays
 * expired, the next request 401s, and refresh is attempted again.
 */
export const isTransientRefreshFailure = (err: any): boolean => {
  const status = err?.response?.status;
  if (status === undefined) return true;
  return status === 429 || status >= 500;
};

/**
 * Is this a SIGN-IN step (OTP start/verify, the social code exchange)?
 *
 * ⚠️ A 401 here is the server refusing that step — a wrong or superseded code, a failed
 * exchange — not a session expiring: there is no session yet. Refreshing cannot help, and on
 * web the refresh itself 401s (no cookie), which used to run clearAuthData() + auth:logout:
 * the full sign-out wiped the in-flight login and threw the user off the code screen, and
 * their next "send code" landed inside the 60 s resend cooldown (prod 2026-10-27). The portal
 * skips these paths the same way (its `isAuthPath`, F-917). Surface the kernel's error instead.
 */
export const isSignInCall = (url?: string): boolean =>
  !!url && (url.includes("/auth/login/") || url.includes("/auth/social/callback"));

let refreshPromise: Promise<boolean> | null = null;

/**
 * Refresh the session ONCE, however many callers ask at the same time.
 *
 * ⚠️ This coalescing is load-bearing, not tidiness. The Cognito app client has
 * **refresh-token rotation ON with a 0-second grace period**: the first refresh rotates
 * the token and immediately invalidates the one that was presented. So two concurrent
 * refreshes mean the second presents a dead token, fails, and tips the user into
 * clearAuthData() + auth:logout — logged out even though the first refresh SUCCEEDED.
 *
 * Previously only the WEB branches coalesced; both native branches refreshed inline and
 * independently, guarded only by `originalRequest._retry`, which is per-request and gives
 * no cross-request exclusion at all. That is the bug this closes.
 *
 * Do not inline this back into the interceptors.
 */
export const refreshSession = (): Promise<boolean> => {
  if (!refreshPromise) {
    refreshPromise = (async (): Promise<boolean> => {
      if (isWeb) {
        // Web: the refresh token rides an HttpOnly cookie; nothing to send in the body.
        // ⚠️ THE KERNEL, not blaze. The session is Cognito's now: posting a Cognito
        // refresh token to blaze cannot work, and the failure is not cosmetic -- refresh
        // returns false, which trips clearAuthData() + auth:logout. With a 15-minute
        // access token that means EVERY session dies 15 minutes after login.
        await axios.post(
          `${KERNEL_URL}/auth/refresh`,
          {},
          { withCredentials: true, headers: { "X-Client": X_CLIENT } },
        );
        // ⚠️ A 2xx IS the success signal on web (F-885; same fix as iss-102 on the app line).
        // By design (HIPAA: web tokens live only in HttpOnly cookies) the Kernel answers a good
        // web refresh with 200 + Set-Cookie and `access_token: ""` in the body: the rotated
        // token is in the cookie, not here. Demanding a body token made this return false, the
        // interceptors then skipped the retry, and the 401'd call failed — Settings dropped
        // CARE TEAM on a stale token. A non-2xx never reaches this line: axios rejects it, and
        // the callers' catch keeps the existing sign-out-on-dead-session path.
        // The socket authenticates by that cookie on web, so it too must re-handshake.
        window.dispatchEvent(new CustomEvent("auth:token-refreshed"));
        return true;
      }
      const refreshToken = await tokenStore.getRefreshToken();
      if (!refreshToken) {
        return false;
      }
      // ⚠️ X-Client is REQUIRED on refresh, not decoration. This is a bare axios call, so it
      // does not inherit kernelClient's headers. Once Stage 3 points refresh at the kernel, a
      // native refresh WITHOUT it falls back to Origin — and on Android that Origin IS the web
      // origin — so the kernel would set cookies and blank the body, `r.data.token` would be
      // undefined, refresh would return false, and the user would be logged out.
      // THE KERNEL (see the web branch above for why this matters).
      // The kernel's field is `refresh_token` in and `access_token` out; blaze used
      // `refreshToken`/`token`. Accept both while the parallel run lasts so an
      // already-installed build's stored shape does not strand anyone.
      const r = await axios.post(
        `${KERNEL_URL}/auth/refresh`,
        { refresh_token: refreshToken, refreshToken },
        { headers: { "X-Client": X_CLIENT } },
      );
      const newAccess = r.data.access_token || r.data.token;
      if (!newAccess) {
        return false;
      }
      await tokenStore.setTokens(newAccess, r.data.refresh_token || r.data.refreshToken);
      // Keep the in-memory defaults in step for requests built before the retry.
      apiClient.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
      uploadClient.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
      kernelClient.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
      // Tell anything holding a COPY of the token that it is now stale. The socket is the
      // one that matters: socket.io v2 captures the credential at handshake time, so a
      // refreshed session leaves it authenticated with a token the server will stop
      // honouring — and it does not fail immediately, it fails whenever the server next
      // re-checks, which looks like "realtime randomly stopped". Mirrors the existing
      // `auth:logout` event convention rather than inventing a second mechanism.
      window.dispatchEvent(new CustomEvent("auth:token-refreshed"));
      return true;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// Create axios instance with default config
export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: isWeb,
});

// Request interceptor to add auth token to requests
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Add timing tracking for error reporting
      config = enhancedRequestInterceptor(config);

      const status = await Network.getStatus();
      // console.log("Network status:", status);
      if (!status.connected) {
        // Optionally, you can show a toast or notification here
        return Promise.reject(new Error("No network connection"));
      }
      // Transport boundary lives in tokenStore: web sends the HttpOnly cookie and gets
      // {} back here; native gets a Bearer header. Do not branch on platform inline.
      Object.assign(config.headers, await tokenStore.authHeader());

      // If the URL includes "http", use CapacitorHttp instead
      if (config.url?.startsWith("http")) {
        const options = {
          url: config.url,
          headers: config.headers,
          params: config.params,
          data: config.data,
          method: config.method?.toUpperCase(),
        };

        const response: HttpResponse = await CapacitorHttp.request(options);
        config.data = response.data;
        config.headers = response.headers as any; // Cast to match AxiosRequestHeaders
        return config;
      }

      return config;
    } catch (error) {
      console.error("Error in request interceptor:", error);
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh and errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // ADDED: Log API errors to Crashlytics silently (production only)
    await enhancedResponseInterceptor(error).catch(() => {
      // Error is intentionally ignored to avoid breaking the flow
    });

    const originalRequest = error.config;

    // If error is 401 (unauthorized) and we haven't tried to refresh yet. Not for a sign-in
    // step: see isSignInCall.
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isSignInCall(originalRequest.url)
    ) {
      originalRequest._retry = true;

      try {
        // ONE refresh, however many requests 401 at the same moment. See refreshSession:
        // Cognito rotates the refresh token with a 0s grace period, so a second concurrent
        // refresh presents a dead token and logs the user out despite the first succeeding.
        const refreshed = await refreshSession();
        if (refreshed) {
          // Re-stamp from the store: the token may have rotated during the wait.
          const header = await tokenStore.authHeader();
          if (header.Authorization) {
            originalRequest.headers = { ...originalRequest.headers, ...header };
          }
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        if (isTransientRefreshFailure(refreshError)) {
          // Rate-limited or upstream trouble: NOT an auth verdict. Keep the session.
          console.warn("Refresh temporarily unavailable; keeping the session", refreshError);
        } else {
          console.error("Error refreshing token:", refreshError);
          await clearAuthData();
          window.dispatchEvent(new CustomEvent("auth:logout"));
        }
      }
    }

    return Promise.reject(error);
  }
);

// Create dedicated upload client without default Content-Type
export const uploadClient = axios.create({
  baseURL: BASE_URL,
  timeout: 60000,
  headers: {
    "Content-Type": "multipart/form-data",
    Accept: "application/json",
  },
  withCredentials: isWeb,
});

// Add auth interceptor to upload client
uploadClient.interceptors.request.use(async (config) => {
  try {
    // Add timing tracking for error reporting
    config = enhancedRequestInterceptor(config);

    // Check network connection
    const status = await Network.getStatus();
    if (!status.connected) {
      return Promise.reject(new Error("No network connection"));
    }

    config.headers = config.headers || {};
    Object.assign(config.headers, await tokenStore.authHeader());

    return config;
  } catch (error) {
    console.error("Error in upload request interceptor:", error);
    return config;
  }
});

// Add token refresh interceptor (same as your main client)
uploadClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // ADDED: Log API errors to Crashlytics silently (production only)
    // Error is intentionally ignored to avoid breaking the flow
    await enhancedResponseInterceptor(error).catch(() => {
      // Suppress error intentionally
    });

    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // ONE refresh, however many requests 401 at the same moment. See refreshSession:
        // Cognito rotates the refresh token with a 0s grace period, so a second concurrent
        // refresh presents a dead token and logs the user out despite the first succeeding.
        const refreshed = await refreshSession();
        if (refreshed) {
          // Re-stamp from the store: the token may have rotated during the wait.
          const header = await tokenStore.authHeader();
          if (header.Authorization) {
            originalRequest.headers = { ...originalRequest.headers, ...header };
          }
          return uploadClient(originalRequest);
        }
      } catch (refreshError) {
        if (isTransientRefreshFailure(refreshError)) {
          // Rate-limited or upstream trouble: NOT an auth verdict. Keep the session.
          console.warn("Refresh temporarily unavailable; keeping the session", refreshError);
        } else {
          console.error("Error refreshing token:", refreshError);
          await clearAuthData();
          window.dispatchEvent(new CustomEvent("auth:logout"));
        }
      }
    }

    return Promise.reject(error);
  }
);

/**
 * The kernel client. Carries `X-Client` (D23 / ADR-0016) so the kernel classifies the
 * transport explicitly rather than inferring it from `Origin` — Android's WebView origin is
 * byte-identical to the production web origin, so `Origin` alone cannot tell them apart.
 * The value is platform-derived (see X_CLIENT); hardcoding it would hand a web build body
 * tokens instead of HttpOnly cookies.
 *
 * Shares `refreshSession` with the blaze clients: one session, one refresh, regardless of
 * which backend returned the 401.
 *
 * ⚠️ Unused until Stage 3 moves the auth calls over. Declared now so the split exists and
 * the web port has both sides to copy (D6).
 */
export const kernelClient = axios.create({
  baseURL: KERNEL_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Client": X_CLIENT,
  },
  withCredentials: isWeb,
});

kernelClient.interceptors.request.use(async (config) => {
  if (!KERNEL_URL) {
    throw new Error(
      "KERNEL_URL is not configured for this build. Set REACT_APP_KERNEL_URL, or add the " +
        "origin to KERNEL_ENVIRONMENTS in services/api.ts. Refusing to fall back to the " +
        "blaze origin, which would 404 confusingly instead of failing here.",
    );
  }
  config.headers = config.headers || {};
  Object.assign(config.headers, await tokenStore.authHeader());
  return config;
});

kernelClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // The kernel returns 403 CONSENT_REQUIRED on EVERY authenticated route except a
    // five-route allowlist until the required documents are accepted (ADR-0011). Surface it
    // once, centrally: without this the block presents as unrelated failures on whatever
    // screen the user happens to be on, with nothing telling them why.
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === "CONSENT_REQUIRED"
    ) {
      window.dispatchEvent(new CustomEvent("auth:consent-required"));
    }

    const originalRequest = error.config;
    // Not for a sign-in step (a wrong or superseded code): see isSignInCall.
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isSignInCall(originalRequest.url)
    ) {
      originalRequest._retry = true;
      try {
        if (await refreshSession()) {
          const header = await tokenStore.authHeader();
          if (header.Authorization) {
            originalRequest.headers = { ...originalRequest.headers, ...header };
          }
          return kernelClient(originalRequest);
        }
      } catch (refreshError) {
        if (isTransientRefreshFailure(refreshError)) {
          // Rate-limited or upstream trouble: NOT an auth verdict. Keep the session.
          console.warn("Refresh temporarily unavailable; keeping the session", refreshError);
        } else {
          console.error("Error refreshing token:", refreshError);
          await clearAuthData();
          window.dispatchEvent(new CustomEvent("auth:logout"));
        }
      }
    }
    return Promise.reject(error);
  },
);

/**
 * `fetch` with the session attached and ONE retry through the shared refresh promise.
 *
 * For the handful of calls that genuinely need `fetch` rather than axios — image blobs and
 * file downloads. They must NOT go through `apiClient`: its request interceptor diverts any
 * absolute URL to `CapacitorHttp`, which would change how images load on native.
 *
 * These sites previously built an Authorization header by hand and had **no refresh path at
 * all**. That was near-harmless on a 1-day blaze token and breaks constantly on a 15-minute
 * Cognito access token. This shares `refreshSession`, so it inherits the same
 * rotation-safe coalescing rather than adding a second refresh mechanism.
 */
export const authedFetch = async (
  input: string,
  init: RequestInit = {},
): Promise<Response> => {
  const send = async (): Promise<Response> =>
    fetch(input, {
      ...init,
      headers: {
        ...(init.headers || {}),
        "X-Client": X_CLIENT,
        ...(await tokenStore.authHeader()),
      },
      credentials: isWeb ? "include" : "same-origin",
    });

  const response = await send();
  if (response.status !== 401) {
    return response;
  }
  const refreshed = await refreshSession().catch(() => false);
  return refreshed ? send() : response;
};

/**
 * Clears all authentication data from storage
 */
export const clearAuthData = async (): Promise<void> => {
  try {
    await tokenStore.clearTokens();
    await Preferences.remove({ key: "user" });
  } catch (error) {
    console.error("Error clearing auth data:", error);
  }
};

/**
 * Saves authentication tokens to storage
 * @param authToken JWT auth token
 * @param refreshToken Refresh token (optional)
 */
export const saveAuthTokens = async (
  authToken: string,
  refreshToken?: string
): Promise<void> => {
  try {
    await tokenStore.setTokens(authToken, refreshToken);
  } catch (error) {
    console.error("Error saving auth tokens:", error);
    throw new Error("Failed to save authentication tokens");
  }
};

/**
 * `blazeClient` — the honest name for the original client. `apiClient` remains its alias
 * so the ~40 existing blaze call sites need no edit (D6 blast radius). New blaze calls
 * should prefer this name; the alias goes away when blaze does.
 */
export const blazeClient = apiClient;
